import { namespace, Logger, Model as BaseModel } from '../../../../../FrontendBundle/Resources/js/ofcs';

/**
 * The Model is a special version of the BaseModel. Editor
 * items need a connected parent item to resolve parent specific values. These
 * parent instance can be passed into the overloaded constructor
 *
 * @class Model
 * @extends BaseModel
 * @param {Object} attributes Hash with model attributes
 * @param {Object} [options] Optional hash with model options
 * @param {Ofcs.Editor.Item.AbstractItem} parentItem Parent instance
 */
export default function Model(attributes, options, parentItem) {
    if (parentItem) {
        this.setParentItem(parentItem);
    }

    BaseModel.call(this, attributes, options);
}

// Inherit from BaseModel
Model.prototype = new BaseModel();

// Preserve special constructor
Model.prototype.constructor = Model;

/**
 * Sets the connected parent instance
 *
 * @param {Ofcs.Editor.Item.AbstractItem} parentItem Parent item
 */
Model.prototype.setParentItem = function setParentItem(parentItem) {
    this.parentItem = parentItem;
};

/**
 * @return {Model}
 * @deprecated
 */
Model.prototype.getFoam = function getFoam() {
    Logger.warn('model.getFoam() was called');
    return this.getParentItem();
};

/**
 * Has this item a parent item
 *
 * @return {boolean}
 */
Model.prototype.hasParentItem = function hasParentItem() {
    return !!this.parentItem;
};

/**
 * Returns the connected parent instance
 *
 * @return {Ofcs.Editor.Item.ContainerItem} parentItem Parent item
 */
Model.prototype.getParentItem = function getParentItem() {
    if (!this.parentItem) {
        throw new Error(`No parent item was set on item ${this.cid}, ${this.type}`);
    }
    return this.parentItem;
};

/**
 * Returns the connected parent instance
 *
 * @return {Ofcs.Editor.Item.ContainerItem} parentItem Parent item
 */
Model.prototype.getAncestors = function getAncestors() {
    if (!this.parentItem) {
        return [];
    }
    return this.parentItem.getAncestorsAndSelf();
};

/**
 * Returns the connected parent instance
 *
 * @return {Ofcs.Editor.Item.ContainerItem} parentItem Parent item
 */
Model.prototype.getAncestorsAndSelf = function getAncestors() {
    if (!this.parentItem) {
        return [this];
    }
    return this.parentItem.getAncestorsAndSelf().concat(this);
};

/**
 * Find parent instance recursively by type
 *
 * @param {string} type
 * @return {Ofcs.Editor.Item.ContainerItem} parentItem Parent item
 */
Model.prototype.findParentItem = function findParentItem(type) {
    if (this.type === 'root') {
        return null;
    }
    if (!this.parentItem) {
        throw new Error(`No parent item was set on item ${this.cid}, ${this.type}`);
    }
    if (this.parentItem.type === type) {
        return this.parentItem;
    }

    return this.parentItem.findParentItem(type);
};

/**
 * Find parent instance recursively by type, check type of current item also
 *
 * @param {string} type
 * @return {Model} parentItem Parent item
 */
Model.prototype.findParentItemOrSelf = function findParentItemOrSelf(type) {
    if (this.type === type) {
        return this;
    }

    return this.findParentItem(type);
};

/**
 * Find parent instance recursively by type, check type of current item also
 *
 * @param {Model} item Parent item
 * @return {boolean}
 */
Model.prototype.isChildOf = function isChildOf(item) {
    if (this.hasParentItem() && this.getParentItem() === item) {
        return true;
    }

    return false;
};

/**
 * Checks if this item is a descendant of another item
 *
 * @param {Model} item Parent item
 * @return {boolean}
 */
Model.prototype.isDescendantOf = function isDescendantOf(item) {
    return this.getAncestors().includes(item);
};

// Overriding clone method, to ensure foam is cloned, too.
Model.prototype.clone = function clone() {
    const attributes = { ...this.attributes };

    if (attributes.position) {
        attributes.position = attributes.position.clone();
    }

    return new this.constructor(attributes, {}, this.hasParentItem() ? this.getParentItem() : null);
};

// Add extend method
Model.extend = BaseModel.extend;

namespace('Ofcs.Editor.Item');
window.Ofcs.Editor.Item.Model = Model;
